import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Lock, Mail, ArrowLeft } from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AxonallLogo from '../components/AxonallLogo'
import ForgotPasswordModal from '../components/ForgotPasswordModal'

const HERO_IMAGE =
  "https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg"

const DARK = '#111111'
const CHARCOAL = '#3a3a3a'
const BG = '#f7f7f7'

const TravelAgentLoginPage = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [pendingApplicationId, setPendingApplicationId] = useState(null)
  const welcomeToastShown = useRef(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    const { email, message, applicationId } = location.state || {}
    if (email) {
      setValue('email', email)
    }
    if (applicationId) {
      setPendingApplicationId(applicationId)
    }
    if (message && !welcomeToastShown.current) {
      welcomeToastShown.current = true
      toast.success(message)
    }
    if (location.state && (email || message || applicationId)) {
      navigate(location.pathname, {
        replace: true,
        state: applicationId ? { applicationId } : {}
      })
    }
  }, [location, navigate, setValue])

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const response = await travelAgentService.login(data.email, data.password)
      toast.success(response.message)

      navigate(travelAgentService.getPostLoginPath())
    } catch (error) {
      toast.error(error.message || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] flex flex-col lg:flex-row antialiased" style={{ backgroundColor: BG }}>

      {/* ── Left — full-height image panel (desktop only) ─────── */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative min-h-0 overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          aria-hidden
        />
        {/* Dark overlay — same density as landing page hero */}
        <div className="absolute inset-0 bg-black/70" />

        {/* Back button — top-left, square outlined */}
        <div className="absolute top-7 left-8 z-20">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 border border-white/40 text-white text-[10px] tracking-[0.22em] uppercase font-poppinsRegular px-4 py-2.5 hover:bg-white/10 transition-colors duration-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Back
          </button>
        </div>

        {/* Centered panel content — editorial, no glass card */}
        <div className="relative z-10 flex flex-1 flex-col justify-center px-12 xl:px-16 2xl:px-20 py-16">
          <div className="max-w-sm">
            <p className="text-[11px] tracking-[0.38em] uppercase text-white/55 mb-5 font-poppinsLight">
              AXONALL STUDIO
            </p>
            <h2 className="font-AXONALLPrimeRegular text-4xl xl:text-5xl uppercase tracking-widest text-white leading-tight mb-6">
              Private Advisor Access
            </h2>
            <div className="w-8 h-px bg-white/30 mb-6" />
            <p className="text-sm font-poppinsLight leading-relaxed text-white/70 mb-4">
              Sign in with your approved advisor credentials to search, book, and manage Axonall product in one place.
            </p>
            <p className="text-xs font-poppinsLight leading-relaxed text-white/45">
              Access is reserved for Axonall Reserve advisors. Need help? Contact your Axonall representative.
            </p>
          </div>
        </div>
      </div>

      {/* ── Right — form panel ──────────────────────────────────── */}
      <div
        className="relative flex flex-1 flex-col overflow-hidden"
        style={{ backgroundColor: BG }}
      >
        {/* Mobile: full-bg image with overlay */}
        <div className="pointer-events-none absolute inset-0 z-0 lg:hidden" aria-hidden>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
          />
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(247,247,247,0.97) 70%, #f7f7f7 100%)' }} />
        </div>

        {/* ── Single-row header (matches landing page) ── */}
        <header
          className="relative z-10 bg-white"
          style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }}
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-10 lg:px-16 flex items-center justify-between h-[60px]">
            {/* Logo — left, clicks back to home */}
            <button type="button" onClick={() => navigate('/')}>
              <AxonallLogo size="text-lg sm:text-xl" color="text-gray-900" />
            </button>

            {/* Nav — right */}
            <nav className="flex items-center gap-6 sm:gap-8">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="hidden sm:inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-[10px] tracking-[0.2em] uppercase font-poppinsRegular transition-colors duration-200"
              >
                <ArrowLeft className="h-3 w-3" aria-hidden />
                Home
              </button>
              <span
                className="text-[10px] tracking-[0.2em] uppercase font-poppinsRegular py-2 px-4 sm:px-5"
                style={{ backgroundColor: '#111111', color: 'white' }}
              >
                Sign In
              </span>
            </nav>
          </div>
        </header>

        {/* ── Form body ── */}
        <div className="relative z-10 flex flex-1 flex-col justify-center px-6 sm:px-10 py-10">
          <div className="mx-auto w-full max-w-md">

            {/* Heading block */}
            <div className="mb-8 text-center lg:text-left">
              <p className="text-[11px] tracking-[0.3em] uppercase font-poppinsLight mb-3 text-white/70 lg:text-[#3a3a3a]">
                STUDIO SIGN IN
              </p>
              <h1 className="font-AXONALLPrimeRegular text-3xl sm:text-4xl uppercase tracking-wider leading-tight text-white lg:text-[#111111]">
                Welcome Back
              </h1>
              <p className="mt-2 text-sm font-poppinsLight text-white/60 lg:text-gray-500">
                Enter your email and password to continue.
              </p>
            </div>

            {/* Form card — thin border, square corners, no heavy shadow */}
            <div
              className="border border-gray-200 bg-white p-7 sm:p-8"
              style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
            >
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block mb-2 text-[10px] tracking-[0.22em] uppercase font-poppinsRegular"
                    style={{ color: CHARCOAL }}
                  >
                    Email
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Mail className="h-[17px] w-[17px] text-gray-400" aria-hidden />
                    </div>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 bg-white text-sm font-poppinsLight text-gray-900 placeholder-gray-400 outline-none focus:border-gray-900 transition-colors duration-200"
                      placeholder="you@example.com"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Please enter a valid email address'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1.5 text-[11px] text-red-500 font-poppinsLight">{errors.email.message}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="password"
                      className="block text-[10px] tracking-[0.22em] uppercase font-poppinsRegular"
                      style={{ color: CHARCOAL }}
                    >
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-[11px] font-poppinsLight text-gray-500 hover:text-gray-900 underline underline-offset-4 decoration-gray-300 hover:decoration-gray-900 transition-colors duration-200"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Lock className="h-[17px] w-[17px] text-gray-400" aria-hidden />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className="w-full pl-10 pr-11 py-3 border border-gray-200 bg-white text-sm font-poppinsLight text-gray-900 placeholder-gray-400 outline-none focus:border-gray-900 transition-colors duration-200"
                      placeholder="••••••••"
                      {...register('password', {
                        required: 'Password is required'
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-gray-700 transition-colors duration-200"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword
                        ? <EyeOff className="h-[17px] w-[17px]" />
                        : <Eye className="h-[17px] w-[17px]" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1.5 text-[11px] text-red-500 font-poppinsLight">{errors.password.message}</p>
                  )}
                </div>

                {/* Submit — same style as landing page CTA buttons */}
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{ backgroundColor: isLoading ? CHARCOAL : DARK }}
                  className="w-full flex items-center justify-center py-4 text-white text-[11px] tracking-[0.3em] uppercase font-poppinsRegular hover:opacity-90 transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-60 mt-2"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

            <div className="mt-7">
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center" aria-hidden>
                  <div className="w-full border-t border-neutral-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-[10px] font-poppinsSemiBold uppercase tracking-[0.2em] text-neutral-400">
                    New to Axonall?
                  </span>
                </div>
              </div>
              <p className="mt-4 text-center text-sm text-neutral-600 font-poppinsRegular">
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="text-primary-900 hover:text-primary-700 font-poppinsMedium underline"
                >
                  Apply for Axonall Reserve
                </button>
              </p>
              {pendingApplicationId && (
                <p className="mt-3 text-center text-sm text-neutral-600 font-poppinsRegular">
                  <Link
                    to="/application-pending"
                    state={{ applicationId: pendingApplicationId }}
                    className="text-primary-900 hover:text-primary-700 font-poppinsMedium underline"
                  >
                    View application status
                  </Link>
                </p>
              )}
            </div>
          </div>

            {/* Footer */}
            <footer className="mt-6 text-center lg:text-left space-y-2">
              <p className="text-[11px] font-poppinsLight text-gray-400">
                © {new Date().getFullYear()} AXONALL Luxury Travel. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-1 text-[11px] font-poppinsLight text-gray-400">
                <span className="hover:text-gray-700 cursor-default transition-colors">Privacy</span>
                <span className="hover:text-gray-700 cursor-default transition-colors">Terms</span>
                <span className="hover:text-gray-700 cursor-default transition-colors">Support</span>
              </div>
            </footer>

          </div>
        </div>
      </div>

      <ForgotPasswordModal isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
    </div>
  )
}

export default TravelAgentLoginPage
